/**
 * Dashboard-Wächter (bisheriges Portal AD-214 … AD-224): Prüfungen über Monate und Rechnungen, die auf
 * Lücken und Unstimmigkeiten hinweisen. Rein; die Oberfläche zeigt die Befunde mit Link zur Stelle.
 */
import { addDays, diffDays } from "./periods.js";
import { normalizeInvoiceNumber } from "./invoice-number.js";
import { resolveCommissionRate } from "./split.js";
import { dealBpForMonth } from "./post-term.js";
import { brandDiscountCents, expectedCreatorNetCents, extraInPeriod, type BrandDiscount, type CommissionTier } from "./kooperationen.js";
import { invoicePeriodIds, type ImportedState } from "./overview.js";
import type { Cents, IsoDate, MonthKey } from "./types.js";

export interface IntegrityDeal {
  id: string;
  talentId: string;
  model: string;
  status: string;
  commissionPercent: number | null;
  /** Gestaffelte Provision und Abzug der Marke (0060): für den erwarteten Betrag des Creators */
  commissionTiers?: readonly CommissionTier[] | null;
  brandDiscount?: BrandDiscount | null;
  /** Nachlaufregel nach Austritt (0074): Satz je Abrechnungsmonat statt commissionPercent (AD-218 Nebenbefund) */
  postTerm?: unknown;
  postTermFrom?: string | null;
  postTermUntil?: string | null;
  /** „share“: die Beträge der Kooperation sind schon der Anteil des Creators (0079), er zieht keine Provision ab */
  amountsBasis?: string | null;
}
export interface IntegrityPeriod {
  id: string;
  dealId: string;
  month: MonthKey;
  amountCents: Cents | null;
  invoiceRequestedAt: string | null;
  commissionForecastCents?: Cents | null;
  /** Storniert ohne Honorar: keine Rechnung, keine Anforderung, kein Umsatz */
  cancelled?: boolean;
  /** Ausfallhonorar oder Minderung: bleibt abrechenbar, auch bei stornierter Kooperation */
  cancellationFee?: boolean;
  /** Im bisherigen System abgerechnet bzw. bezahlt (0064): gilt als an die Marke abgerechnet (PRAXIS-R3-1) */
  importedState?: ImportedState | null;
}
export interface IntegrityExtra {
  /** Kooperation der Position; nötig für Positionen ohne Monat („Ganze Kooperation“ gehört zum ersten Monat, GELD-8) */
  dealId?: string | null;
  periodId: string | null;
  amountCents: Cents;
  commissionable: boolean;
}
export interface IntegrityInvoice {
  id: string;
  kind: "talent_to_agency" | "agency_to_brand" | "talent_to_brand" | "agency_to_talent";
  dealId: string | null;
  periodId: string | null;
  talentId: string | null;
  number: string | null;
  status: string;
  netCents: Cents | null;
  createdAt: string;
  dueDate: IsoDate | null;
  revenueMonth: string | null;
  rejectionMailSentAt: string | null;
  paidAt?: string | null;
  /** Sammelrechnung an die Marke (invoice_periods): weitere Monate, die sie abrechnet */
  periodIds?: readonly string[];
  /** Stornorechnung zu (credit_for): zählt nicht als zweite Rechnung derselben Leistung */
  creditFor?: string | null;
  /** Satz, mit dem der Creator die Provision abgezogen hat (0161, Basispunkte); null = kein Abzug */
  commissionBpApplied?: number | null;
  /** Netto der provisionspflichtigen Positionen; ohne Angabe ist „ohne Abzug“ nicht prüfbar (Auslagen sind provisionsfrei) */
  commissionableNetCents?: Cents | null;
  /** Beginn des Leistungszeitraums: Abrechnungsmonat einer Rechnung ohne Monat für den Nachlaufsatz */
  servicePeriodStart?: string | null;
}
export interface IntegrityInput {
  today: IsoDate;
  deals: readonly IntegrityDeal[];
  periods: readonly IntegrityPeriod[];
  extras: readonly IntegrityExtra[];
  invoices: readonly IntegrityInvoice[];
  agencyCommissionPercent: number;
  talentCommissionPercent: Readonly<Record<string, number | null>>;
  /** Agenturanteil je Monat (aus der Übersicht), für „nicht abgerechnet“ */
  managementByPeriod?: ReadonlyMap<string, Cents>;
  /** Nur Befunde zu diesem Creator (Filter der Übersicht) */
  talentId?: string | null;
  /** Einstellungen der Agentur; ohne Angabe gelten die Produkt-Standards */
  staleDraftDays?: number;
  /** Ab so vielen Tagen ist eine angeforderte, fehlende Rechnung dringend */
  urgentDays?: number;
  /** Erlaubte Abweichung des Creator-Betrags in Prozent (org_settings.budget_deviation_percent) */
  deviationPercent?: number;
  /** Auszahlung an Creator erst nach Zahlungseingang der Marke plus Frist (org_settings.talent_payout_mode) */
  payoutAfterBrandPayment?: boolean;
  talentPaymentTermsDays?: number;
  /**
   * Verträge der Kooperationen (Status aus contracts). Mit Angabe prüft der Wächter „abgerechnet ohne unterschriebenen Vertrag“
   * (Hinweis; viele Agenturen arbeiten mit Mail-Zusagen, deshalb ohne Angabe keine Prüfung).
   */
  /**
   * Verträge je Kooperation. endsOn: Ende eines befristeten oder gekündigten Vertrags (ohne automatische Verlängerung);
   * Leistungsmonate danach deckt der Vertrag nicht mehr (VERTRAEGE-R2-05). Ohne Angabe gilt er unbegrenzt.
   */
  contracts?: readonly { dealId: string | null; status: string; endsOn?: string | null }[];
  /** Vertragshinweis nur für Rechnungen ab diesem Leistungsmonat (ältere Abrechnungen sind erledigt); ohne Angabe alle */
  contractsSinceMonth?: MonthKey;
}

export type IntegrityKind =
  | "requested_missing"
  | "stale_draft"
  | "amount_mismatch"
  | "duplicate_number"
  | "talent_overdue"
  | "correction_no_mail"
  | "duplicate_service"
  | "commission_without_month"
  | "missing_revenue_month"
  | "commission_unknown"
  | "billed_without_contract"
  | "partially_billed";

export interface IntegrityFinding {
  key: string;
  kind: IntegrityKind;
  severity: "error" | "warning" | "info";
  dealId: string | null;
  periodId: string | null;
  invoiceId: string | null;
  talentId: string | null;
  month: MonthKey | null;
  days?: number;
  expectedCents?: Cents;
  actualCents?: Cents;
  number?: string;
  /** duplicate_service: Nummern der anderen Rechnungen derselben Leistung */
  others?: string[];
  /** commission_without_month: vereinbarter und angewandter Satz in Prozent (0 = kein Abzug) */
  expectedPercent?: number;
  actualPercent?: number;
}

export interface IntegrityResult {
  findings: IntegrityFinding[];
  /** Agenturanteil vergangener und laufender Monate ohne Rechnung an die Marke */
  notInvoicedCents: Cents;
  notInvoicedPeriods: number;
  /** Die betroffenen Monate (für Links zur Stelle), ältester zuerst */
  notInvoiced: { periodId: string; dealId: string; talentId: string; month: MonthKey; cents: Cents }[];
}

const INACTIVE = new Set(["cancelled", "withdrawn", "discarded", "rejected"]);
/**
 * Zählt nicht als Abrechnung: stornierte, zurückgezogene, verworfene und abgelehnte Rechnungen und ebenso Stornorechnungen
 * (credit_for). Eine Stornorechnung hebt ihre Rechnung nur auf; mit ihrem negativen Betrag wäre der Monat sonst „an die Marke
 * abgerechnet: -1.350,03 €“ (Praxistest K-149).
 */
const inactive = (i: { status: string; creditFor?: string | null }) => INACTIVE.has(i.status) || !!i.creditFor;
const day = (s: string) => s.slice(0, 10);
/** Ab diesen Kooperationsstatus gilt ein Monat im laufenden Monat schon als abrechenbar */
const BILLABLE_STATUS = new Set(["published", "reporting", "invoicing", "done"]);

/**
 * Fälligkeit der Auszahlung einer Creator-Rechnung: fest aus der Rechnung, oder (Auszahlung nach Zahlungseingang)
 * Zahlungseingang der Marke für denselben Monat plus Frist; null = noch nicht fällig, weil die Marke nicht gezahlt hat.
 */
export function payoutDueDate(i: Pick<IntegrityInvoice, "dueDate" | "periodId">, a: {
  invoices: readonly Pick<IntegrityInvoice, "kind" | "periodId" | "status" | "paidAt">[]; payoutAfterBrandPayment?: boolean; talentPaymentTermsDays?: number;
}): IsoDate | null {
  if (!a.payoutAfterBrandPayment || !i.periodId) return i.dueDate;
  const paid = a.invoices.find((x) => x.kind === "agency_to_brand" && x.periodId === i.periodId && x.status === "paid" && x.paidAt);
  return paid ? addDays(day(paid.paidAt!), a.talentPaymentTermsDays ?? 0) : null;
}

export function integrityFindings(input: IntegrityInput): IntegrityResult {
  const findings: IntegrityFinding[] = [];
  const deals = new Map(input.deals.map((d) => [d.id, d]));
  const periods = new Map(input.periods.map((p) => [p.id, p]));
  const talentInvoices = input.invoices.filter((i) => i.kind === "talent_to_agency" && !inactive(i));
  // Eingereicht zählt nur, was nicht als Entwurf liegt und nicht zur Korrektur zurückgegeben ist (AD-215); beim Abrechnungsweg
  // „Creator rechnet direkt an die Marke“ zählt dessen Rechnung an die Marke
  const submittedPeriods = new Set(input.invoices
    .filter((i) => i.kind !== "agency_to_brand" && !inactive(i) && !["draft", "revision_requested"].includes(i.status))
    .map((i) => i.periodId));
  const draftPeriods = new Set(talentInvoices.filter((i) => i.status === "draft").map((i) => i.periodId));
  void draftPeriods;

  // Zusatzpositionen eines Monats: mit Monat genau dieser, ohne Monat („Ganze Kooperation“) der erste Monat der Kooperation,
  // wie Aufteilung, Anforderung und Übersicht (GELD-8)
  const periodsOfDeal = new Map<string, IntegrityPeriod[]>();
  for (const p of input.periods) periodsOfDeal.set(p.dealId, [...(periodsOfDeal.get(p.dealId) ?? []), p]);
  const extrasOf = (p: IntegrityPeriod) => input.extras.filter((e) => e.periodId === p.id
    || (e.dealId === p.dealId && extraInPeriod(e.periodId, p.id, periodsOfDeal.get(p.dealId) ?? [])));

  // Stornierte Monate und stornierte Kooperationen (außer Monaten mit Ausfallhonorar) sind abgeschlossen
  const closed = (p: IntegrityPeriod, d: IntegrityDeal) => !!p.cancelled || (d.status === "cancelled" && !p.cancellationFee);

  // AD-215: angeforderte Monate ohne eingereichte Rechnung
  for (const p of input.periods) {
    if (!p.invoiceRequestedAt || submittedPeriods.has(p.id)) continue;
    const d = deals.get(p.dealId);
    if (!d || closed(p, d)) continue;
    const days = Math.max(0, diffDays(day(p.invoiceRequestedAt), input.today));
    findings.push({ key: `requested:${p.id}`, kind: "requested_missing", severity: days > (input.urgentDays ?? 14) ? "error" : "warning", dealId: p.dealId, periodId: p.id,
      invoiceId: null, talentId: d.talentId, month: p.month.slice(0, 7), days });
  }

  // AD-216: liegengebliebene Entwürfe (ohne zugehörigen fehlenden Monat)
  const stale = input.staleDraftDays ?? 7;
  for (const i of talentInvoices) {
    if (i.status !== "draft" || (i.periodId && draftPeriods.has(i.periodId) && findings.some((f) => f.periodId === i.periodId))) continue;
    const days = diffDays(day(i.createdAt), input.today);
    if (days >= stale) findings.push({ key: `draft:${i.id}`, kind: "stale_draft", severity: "info", dealId: i.dealId, periodId: i.periodId, invoiceId: i.id,
      talentId: i.talentId, month: i.revenueMonth?.slice(0, 7) ?? null, days });
  }

  // Satz der Kooperation für einen Abrechnungsmonat: Nachlaufregel (0074) vor deals.commission_bp, dann Creator, dann Agentur
  const rateFor = (d: IntegrityDeal, month: string | null): number => {
    const bp = dealBpForMonth({ commission_bp: d.commissionPercent == null ? null : Math.round(d.commissionPercent * 100), post_term: d.postTerm,
      post_term_from: d.postTermFrom ?? null, post_term_until: d.postTermUntil ?? null }, month);
    return resolveCommissionRate({ deal: bp == null ? null : bp / 100, talent: input.talentCommissionPercent[d.talentId] ?? null, agency: input.agencyCommissionPercent }).percent;
  };

  // AD-217/218: Betrag des Creators gegen die Vereinbarung (Budget × (1 − Satz) + durchlaufende Posten)
  for (const i of talentInvoices) {
    if (i.status === "draft" || !i.periodId || i.netCents == null) continue;
    const p = periods.get(i.periodId);
    const d = p ? deals.get(p.dealId) : undefined;
    if (!p || !d || p.cancelled || d.model === "commission_only" || d.model === "custom" || p.amountCents == null) continue;
    const rate = rateFor(d, p.month);
    const extras = extrasOf(p);
    const commissionable = extras.filter((e) => e.commissionable).reduce((a, e) => a + e.amountCents, 0);
    const passThrough = extras.filter((e) => !e.commissionable).reduce((a, e) => a + e.amountCents, 0);
    // Anteil des Creators nach Satz oder Staffel und nach dem Abzug der Marke (0060)
    const expected = expectedCreatorNetCents({ honorarCents: p.amountCents + commissionable, passThroughCents: passThrough,
      rate: { percent: rate, tiers: d.commissionTiers ?? null }, discount: d.brandDiscount ?? null });
    // Ein geringerer Betrag mit weggelassenen durchlaufenden Posten gilt ebenfalls als stimmig
    const alt = expected - passThrough;
    // Toleranz: die erlaubte Abweichung der Agentur (Prozent), mindestens 2 Cent für Rundungen
    const tolerance = Math.max(2, Math.round((Math.abs(expected) * (input.deviationPercent ?? 0)) / 100));
    if (Math.abs(i.netCents - expected) > tolerance && Math.abs(i.netCents - alt) > tolerance) {
      findings.push({ key: `amount:${i.id}`, kind: "amount_mismatch", severity: "warning", dealId: d.id, periodId: p.id, invoiceId: i.id, talentId: d.talentId,
        month: p.month.slice(0, 7), expectedCents: expected, actualCents: i.netCents, ...(i.number ? { number: i.number } : {}) });
    }
  }

  // AD-218 (0161): Rechnungen ohne Monat (Nachberechnung, Auslagen) gegen den Provisionssatz. Gespeichert sind nur die Positionen
  // nach Abzug; der Satz des Abzugs steht an der Rechnung (commission_bp_applied). Ein abweichender Satz ist ein Befund; kein Abzug
  // trotz provisionspflichtiger Positionen ebenso (Auslagen sind provisionsfrei, Kooperationen mit Anteilsbeträgen ziehen nichts ab).
  for (const i of talentInvoices) {
    if (i.status === "draft" || i.periodId || !i.dealId) continue;
    const d = deals.get(i.dealId);
    if (!d || d.model === "commission_only" || d.model === "custom" || d.amountsBasis === "share") continue;
    const month = (i.revenueMonth ?? i.servicePeriodStart ?? i.createdAt).slice(0, 7);
    let rate: number;
    try { rate = rateFor(d, month); } catch { continue; }
    const tiers = (d.commissionTiers ?? []).length > 0;
    const applied = i.commissionBpApplied == null ? null : i.commissionBpApplied / 100;
    const base = { key: `commission:${i.id}`, kind: "commission_without_month" as const, dealId: d.id, periodId: null, invoiceId: i.id, talentId: d.talentId,
      month: i.revenueMonth?.slice(0, 7) ?? null, expectedPercent: rate, ...(i.number ? { number: i.number } : {}) };
    if (applied != null) {
      // Mit Staffel gibt es keinen einzelnen Satz; dann nur der Fall „kein Abzug“
      if (!tiers && Math.abs(applied - rate) > 0.001) findings.push({ ...base, severity: "warning", actualPercent: applied });
    } else if (rate > 0 && (i.commissionableNetCents ?? 0) > 0 && ["submitted", "revision_requested", "approved", "sent"].includes(i.status)) {
      // „Ohne Abzug“ nur bis zur Zahlung: ältere Rechnungen (vor 0161 ohne gespeicherten Satz) bleiben danach still
      findings.push({ ...base, severity: "warning", actualPercent: 0 });
    }
  }

  // AD-134/RE-40 (0161): zweite wirksame Creator-Rechnung für dieselbe Kooperation und denselben Monat
  // Monat = zugeordneter Kooperationsmonat; Rechnungen ohne Monat (Nachberechnung, Auslagen, Leistungsmonat nur aus dem Zeitraum) zählen nicht
  const monthOfInvoice = (i: IntegrityInvoice) => (i.periodId ? periods.get(i.periodId)?.month ?? i.revenueMonth : null);
  const dups = findCreatorInvoiceDuplicates(input.invoices.map((i) => ({ id: i.id, kind: i.kind, dealId: i.dealId, month: monthOfInvoice(i), status: i.status,
    creditFor: i.creditFor ?? null })));
  const numberOf = new Map(input.invoices.map((i) => [i.id, i.number]));
  for (const i of input.invoices) {
    const others = dups.get(i.id);
    if (!others) continue;
    findings.push({ key: `dupservice:${i.id}`, kind: "duplicate_service", severity: "error", dealId: i.dealId, periodId: i.periodId, invoiceId: i.id,
      talentId: i.talentId, month: monthOfInvoice(i)?.slice(0, 7) ?? null, ...(i.number ? { number: i.number } : {}),
      others: others.map((o) => numberOf.get(o) ?? "").filter((n): n is string => !!n) });
  }

  // AD-219: doppelte Rechnungsnummern je Aussteller
  const groups = new Map<string, IntegrityInvoice[]>();
  for (const i of input.invoices) {
    if (!i.number || INACTIVE.has(i.status)) continue;
    // Agenturrechnungen (an Marken und an Creator) teilen einen Nummernkreis
    const issuer = i.kind === "agency_to_brand" || i.kind === "agency_to_talent" ? "agency" : `talent:${i.talentId}`;
    const k = `${issuer}|${normalizeInvoiceNumber(i.number)}`;
    groups.set(k, [...(groups.get(k) ?? []), i]);
  }
  for (const list of groups.values()) {
    if (list.length < 2) continue;
    for (const i of list) findings.push({ key: `dup:${i.id}`, kind: "duplicate_number", severity: "error", dealId: i.dealId, periodId: i.periodId, invoiceId: i.id,
      talentId: i.talentId, month: i.revenueMonth?.slice(0, 7) ?? null, number: i.number! });
  }

  // AD-220: Auszahlungen an Creator, deren Fälligkeit verstrichen ist (Hinweis); bei „Auszahlung nach Zahlungseingang“
  // erst, wenn die Marke gezahlt hat und die Frist danach verstrichen ist
  for (const i of talentInvoices) {
    if (!["approved", "sent"].includes(i.status)) continue;
    const due = payoutDueDate(i, input);
    if (!due || due >= input.today) continue;
    findings.push({ key: `talentdue:${i.id}`, kind: "talent_overdue", severity: "info", dealId: i.dealId, periodId: i.periodId, invoiceId: i.id,
      talentId: i.talentId, month: i.revenueMonth?.slice(0, 7) ?? null, days: diffDays(due, input.today), ...(i.number ? { number: i.number } : {}) });
  }

  // Nur Provision: vergangener Monat ohne erwartete Provision und ohne Agenturrechnung (zählt sonst still mit 0)
  const agencyBilled = new Set(input.invoices.filter((i) => i.kind === "agency_to_brand" && !inactive(i) && i.status !== "draft").flatMap((i) => invoicePeriodIds(i)));
  // Im bisherigen System abgerechnete Monate (übernommen) sind abgerechnet, auch ohne Rechnung im Portal (PRAXIS-R3-1)
  for (const p of input.periods) if (p.importedState) agencyBilled.add(p.id);
  for (const p of input.periods) {
    const d = deals.get(p.dealId);
    if (!d || d.model !== "commission_only" || closed(p, d) || p.month.slice(0, 7) >= input.today.slice(0, 7)) continue;
    if (p.commissionForecastCents != null || agencyBilled.has(p.id)) continue;
    findings.push({ key: `commission:${p.id}`, kind: "commission_unknown", severity: "warning", dealId: d.id, periodId: p.id, invoiceId: null,
      talentId: d.talentId, month: p.month.slice(0, 7) });
  }

  // AD-221: Korrektur angefordert, aber keine Mail an den Creator vermerkt
  for (const i of input.invoices) {
    if (i.status === "revision_requested" && !i.rejectionMailSentAt) {
      findings.push({ key: `mail:${i.id}`, kind: "correction_no_mail", severity: "warning", dealId: i.dealId, periodId: i.periodId, invoiceId: i.id,
        talentId: i.talentId, month: i.revenueMonth?.slice(0, 7) ?? null, ...(i.number ? { number: i.number } : {}) });
    }
  }

  // AD-222: Rechnung einer Monatskooperation ohne Leistungsmonat; bei anderen Modellen eine Rechnung, die keinem Monat
  // zugeordnet ist (der Creator kann „ohne Monat“ einreichen): sie zählt sonst in keinem Monatsstand mit
  for (const i of input.invoices) {
    const d = i.dealId ? deals.get(i.dealId) : undefined;
    if (!d || inactive(i) || i.status === "draft") continue;
    if (d.model === "monthly" ? !!i.revenueMonth : !!i.periodId || !!i.revenueMonth) continue;
    findings.push({ key: `month:${i.id}`, kind: "missing_revenue_month", severity: "warning", dealId: d.id, periodId: i.periodId, invoiceId: i.id,
      talentId: i.talentId, month: null, ...(i.number ? { number: i.number } : {}) });
  }

  // Abgerechnet ohne unterschriebenen Vertrag: eine wirksame Rechnung zur Kooperation, aber kein unterschriebener Vertrag dazu.
  // Sammelrechnungen zählen für jede Kooperation ihrer Monate; mit contractsSinceMonth nur jüngere Abrechnungen (Befund 14)
  if (input.contracts) {
    // je Kooperation der späteste gedeckte Monat (null = ohne Ende); ein beendeter Vertrag deckt spätere Monate nicht
    const coveredUntil = new Map<string, string | null>();
    for (const c of input.contracts) {
      if (!c.dealId || c.status !== "signed") continue;
      const until = c.endsOn ? c.endsOn.slice(0, 7) : null;
      const prev = coveredUntil.get(c.dealId);
      coveredUntil.set(c.dealId, prev === undefined ? until : prev === null || until === null ? null : prev > until ? prev : until);
    }
    const covers = (dealId: string, month: string) => {
      if (!coveredUntil.has(dealId)) return false;
      const until = coveredUntil.get(dealId);
      return until === null || until === undefined || month <= until;
    };
    const seen = new Set<string>();
    for (const i of input.invoices) {
      if (inactive(i) || ["draft", "submitted", "revision_requested"].includes(i.status)) continue;
      if (input.contractsSinceMonth && (i.revenueMonth ?? i.createdAt).slice(0, 7) < input.contractsSinceMonth) continue;
      const dealIds = i.dealId ? [i.dealId] : [...new Set(invoicePeriodIds(i).map((pid) => periods.get(pid)?.dealId).filter((x): x is string => !!x))];
      for (const dealId of dealIds) {
        if (seen.has(dealId) || covers(dealId, (i.revenueMonth ?? i.createdAt).slice(0, 7))) continue;
        const d = deals.get(dealId);
        if (!d || d.status === "cancelled") continue;
        if (input.talentId && d.talentId !== input.talentId) continue;
        seen.add(dealId);
        findings.push({ key: `contract:${d.id}`, kind: "billed_without_contract", severity: "info", dealId: d.id, periodId: i.periodId, invoiceId: null,
          talentId: d.talentId, month: i.revenueMonth?.slice(0, 7) ?? null, ...(i.number ? { number: i.number } : {}) });
      }
    }
  }

  // Nicht abgerechnet: abgeschlossene Monate (oder der laufende, wenn die Leistung schon erbracht ist) ohne wirksame Rechnung an die Marke;
  // eine Sammelrechnung rechnet jeden ihrer Monate ab (Befund 5)
  const liveBrand = input.invoices.filter((i) => i.kind !== "talent_to_agency" && !inactive(i) && i.status !== "draft");
  const billed = new Set(liveBrand.flatMap((i) => invoicePeriodIds(i)));
  for (const p of input.periods) if (p.importedState) billed.add(p.id);
  let notInvoicedCents = 0;
  let notInvoicedPeriods = 0;
  const notInvoiced: IntegrityResult["notInvoiced"] = [];
  const currentMonth = input.today.slice(0, 7);
  for (const p of input.periods) {
    const d = deals.get(p.dealId);
    if (!d || closed(p, d) || d.status === "blocked" || billed.has(p.id)) continue;
    if (input.talentId && d.talentId !== input.talentId) continue;
    const m0 = p.month.slice(0, 7);
    if (m0 > currentMonth || (m0 === currentMonth && !BILLABLE_STATUS.has(d.status))) continue;
    const m = input.managementByPeriod?.get(p.id) ?? 0;
    if (m <= 0) continue;
    notInvoicedCents += m;
    notInvoicedPeriods += 1;
    notInvoiced.push({ periodId: p.id, dealId: d.id, talentId: d.talentId, month: m0, cents: m });
  }
  notInvoiced.sort((a, b) => a.month.localeCompare(b.month));

  // Teilweise abgerechnet (Befund 7): der Monat ist an die Marke abgerechnet, aber später kam etwas hinzu (Nachkauf,
  // Nutzungsrechte-Verlängerung). Nur Rechnungen genau dieses Monats; Sammelrechnungen rechnen nach Positionen ab.
  const collective = new Set(liveBrand.filter((i) => (i.periodIds ?? []).length > 0).flatMap((i) => invoicePeriodIds(i)));
  const brandNet = new Map<string, number>();
  for (const i of liveBrand) {
    if (!i.periodId || i.kind === "agency_to_talent" || i.netCents == null || (i.periodIds ?? []).length) continue;
    brandNet.set(i.periodId, (brandNet.get(i.periodId) ?? 0) + i.netCents);
  }
  for (const [pid, net] of brandNet) {
    const p = periods.get(pid);
    const d = p ? deals.get(p.dealId) : undefined;
    if (!p || !d || p.cancelled || collective.has(pid) || d.model === "commission_only") continue;
    if (input.talentId && d.talentId !== input.talentId) continue;
    const extras = extrasOf(p);
    const commissionable = extras.filter((e) => e.commissionable).reduce((a, e) => a + e.amountCents, 0);
    const passThrough = extras.filter((e) => !e.commissionable).reduce((a, e) => a + e.amountCents, 0);
    const honorar = (p.amountCents ?? 0) + commissionable;
    const expected = honorar - brandDiscountCents(honorar, d.brandDiscount ?? null) + passThrough;
    const tolerance = Math.max(2, Math.round((Math.abs(expected) * (input.deviationPercent ?? 0)) / 100));
    if (expected - net > tolerance) {
      findings.push({ key: `partial:${p.id}`, kind: "partially_billed", severity: "warning", dealId: d.id, periodId: p.id, invoiceId: null, talentId: d.talentId,
        month: p.month.slice(0, 7), expectedCents: expected, actualCents: net });
    }
  }

  const rank = { error: 0, warning: 1, info: 2 } as const;
  if (input.talentId) {
    const keep = findings.filter((f) => f.talentId === input.talentId);
    findings.length = 0;
    findings.push(...keep);
  }
  findings.sort((a, b) => rank[a.severity] - rank[b.severity] || (b.days ?? 0) - (a.days ?? 0));
  return { findings, notInvoicedCents, notInvoicedPeriods, notInvoiced };
}

/**
 * Eingabe der Dublettenprüfung: Monat als YYYY-MM oder Datum, und zwar der zugeordnete Kooperationsmonat (period_id). Eine
 * Nachberechnung trägt als Leistungsmonat nur den Monat ihres Zeitraums und hat keinen Kooperationsmonat: dann month = null.
 */
export interface DuplicateCandidate {
  id: string;
  kind: string;
  dealId: string | null;
  month: string | null;
  status: string;
  creditFor?: string | null;
}

/**
 * AD-134/RE-40: Creator-Rechnungen (an die Agentur oder direkt an die Marke) zur selben Kooperation und zum selben Monat, die
 * beide wirksam sind (nicht Entwurf, nicht verworfen, zurückgezogen, storniert, abgelehnt; keine Stornorechnung). Ergebnis:
 * Rechnungs-ID → die anderen Rechnungen. Rechnungen ohne Monat (Nachberechnung, Auslagen) zählen nicht: davon darf es mehrere geben.
 */
export function findCreatorInvoiceDuplicates(invoices: readonly DuplicateCandidate[]): Map<string, string[]> {
  const groups = new Map<string, string[]>();
  for (const i of invoices) {
    if ((i.kind !== "talent_to_agency" && i.kind !== "talent_to_brand") || !i.dealId || !i.month || i.creditFor) continue;
    if (inactive(i) || i.status === "draft") continue;
    const key = `${i.dealId}|${i.month.slice(0, 7)}`;
    groups.set(key, [...(groups.get(key) ?? []), i.id]);
  }
  const out = new Map<string, string[]>();
  for (const ids of groups.values()) {
    if (ids.length < 2) continue;
    for (const id of ids) out.set(id, ids.filter((x) => x !== id));
  }
  return out;
}

/** Frist, ab der ein angeforderter Monat als lange offen gilt (Anzeige „seit … Tagen“). */
export function requestedSince(requestedAt: string, today: IsoDate): number {
  return Math.max(0, diffDays(day(requestedAt), today));
}
